import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowUpRight,
  BadgeCheck,
  Camera,
  Check,
  CreditCard,
  Eye,
  EyeOff,
  Info,
  Loader2,
  Mail,
  Moon,
  Palette,
  ShieldCheck,
  Sun,
  User as UserIcon,
  Zap,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "./Header";
import SEO from "./SEO";
import ConfirmModal from "./ConfirmModal";
import "./Settings.css";

/* Navigation model. Only sections backed by real functionality are listed. */
const SETTINGS_SECTIONS = [
  {
    id: "account",
    label: "Account",
    description: "Profile, photo and email address",
    Icon: UserIcon,
  },
  {
    id: "appearance",
    label: "Appearance",
    description: "Theme and visual preferences",
    Icon: Palette,
  },
  {
    id: "security",
    label: "Security",
    description: "Password and sign-in method",
    Icon: ShieldCheck,
  },
  {
    id: "billing",
    label: "Billing",
    description: "Plan, usage and invoices",
    Icon: CreditCard,
  },
];

/* Motion contract shared by every panel so section changes feel identical. */
const panelMotion = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: 0.22, ease: [0.32, 0.72, 0, 1] },
};

export default function Settings() {
  const { user, isAuthenticated, updateProfile, verifyToken } = useAuth();
  const { darkMode, setDarkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    role: user?.role || "Attorney",
  });
  const [avatarPreview, setAvatarPreview] = useState(user?.avatarUrl || "");
  const [avatarData, setAvatarData] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);

  // Password change state (for non-Google / non-OAuth users)
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState({
    current: "",
    new: "",
    confirm: "",
  });

  const [status, setStatus] = useState({ type: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [loadingSub, setLoadingSub] = useState(false);
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") === "billing" ? "billing" : "account"
  );
  const [buyingTokens, setBuyingTokens] = useState(false);
  const [tokenTopUpError, setTokenTopUpError] = useState("");

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "", // This will show the new email after verification
        role: user.role || "Attorney",
      });
      setAvatarPreview(user.avatarUrl || "");
      setPendingEmail(user.pendingEmail || null);
      // If email was just verified, ensure the field is locked
      // (This is already handled by emailVerified state, but we ensure consistency)
    }
  }, [user]);

  // Check for email verification status from URL params
  useEffect(() => {
    const emailVerifiedParam = searchParams.get("emailVerified");
    const error = searchParams.get("error");

    if (emailVerifiedParam === "true") {
      // Lock the email field immediately
      setEmailVerified(true);
      setPendingEmail(null); // Clear pending email

      // Show success message
      setStatus({
        type: "success",
        message:
          "Email verified and updated successfully! Your new email address is now active and locked. You can still update your name and profile photo below. Click 'Change Email' if you want to change it again.",
      });

      // Refresh user data to get the updated email from the database
      // The user effect will update formData.email when user object changes
      verifyToken();

      setSearchParams({}); // Clear URL params after processing
    } else if (emailVerifiedParam === "false") {
      setEmailVerified(false);
      setStatus({
        type: "error",
        message: error
          ? decodeURIComponent(error)
          : "Email verification failed. The email address could not be changed. You can still update other fields or request a new verification email.",
      });
      setSearchParams({}); // Clear URL params
      // Refresh user data to get current state
      verifyToken();
    }
  }, [searchParams, setSearchParams, verifyToken]);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscriptionStatus();
    }
  }, [isAuthenticated]);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoadingSub(true);
      const resp = await axios.get(`${API_URL}/payments/subscription-status`, {
        withCredentials: true,
      });
      setSubscription(resp.data);
    } catch (err) {
      console.error("Error fetching subscription status:", err);
    } finally {
      setLoadingSub(false);
    }
  };

  const handleCancelSubscription = () => {
    // Trial cancellations get routed through Pricing so we can offer a
    // last-chance "skip trial & pay now" option before they cancel.
    if (subscription?.subscriptionStatus === "trialing") {
      navigate("/pricing", { state: { promptCancelTrial: true } });
      return;
    }
    setShowCancelConfirm(true);
  };

  const confirmCancelSubscription = async () => {
    const isTrial = subscription?.subscriptionStatus === "trialing";
    try {
      setCanceling(true);
      await axios.post(
        `${API_URL}/stripe/cancel-subscription`,
        {},
        { withCredentials: true },
      );
      setShowCancelConfirm(false);
      setStatus({
        type: "success",
        message: isTrial
          ? "Your free trial has been set to cancel at the end of the current period."
          : "Your subscription has been set to cancel at the end of the current period.",
      });
      fetchSubscriptionStatus();
    } catch (err) {
      console.error("Cancellation error", err);
      const serverMsg =
        err.response?.data?.message || err.response?.data?.error;
      setShowCancelConfirm(false);
      setStatus({
        type: "error",
        message: `Failed to cancel subscription: ${serverMsg || err.message}`,
      });
    } finally {
      setCanceling(false);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      setResuming(true);
      await axios.post(
        `${API_URL}/stripe/resume-subscription`,
        {},
        { withCredentials: true },
      );
      setStatus({
        type: "success",
        message:
          "Your subscription has been resumed and will renew automatically.",
      });
      await fetchSubscriptionStatus();
    } catch (err) {
      console.error("Resume subscription error", err);
      const serverMsg =
        err.response?.data?.message || err.response?.data?.error;
      setStatus({
        type: "error",
        message: `Failed to resume subscription: ${serverMsg || err.message}`,
      });
    } finally {
      setResuming(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const resp = await axios.post(
        `${API_URL}/payments/create-portal-session`,
        {},
        { withCredentials: true },
      );
      const portalUrl = resp?.data?.url;
      if (!portalUrl) {
        throw new Error("No billing portal URL returned");
      }
      window.location.href = portalUrl;
    } catch (err) {
      console.error("Error opening billing portal:", err);
      const serverMsg =
        err.response?.data?.message || err.response?.data?.error;
      alert(
        `Unable to open billing portal: ${serverMsg || err.message || "Please try again."}`,
      );
    }
  };

  const handleBuyTokens = async () => {
    setTokenTopUpError("");
    try {
      setBuyingTokens(true);
      const resp = await axios.post(
        `${API_URL}/stripe/buy-tokens`,
        {},
        { withCredentials: true },
      );
      if (resp.data?.url) {
        window.location.href = resp.data.url;
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        "Failed to start token top-up. Please try again.";
      setTokenTopUpError(msg);
    } finally {
      setBuyingTokens(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Show password field when email is being changed
    if (name === "email" && value !== user?.email && value !== pendingEmail) {
      setShowPasswordField(true);
    } else if (
      name === "email" &&
      (value === user?.email || value === pendingEmail)
    ) {
      setShowPasswordField(false);
      setPassword("");
    }
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      setStatus({
        type: "error",
        message: "File size exceeds 2MB limit. Please choose a smaller image.",
      });
      return;
    }

    // Validate file type
    if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
      setStatus({
        type: "error",
        message: "Invalid file type. Please upload a PNG or JPEG image.",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === "string") {
        setAvatarPreview(result);
        setAvatarData(result);
        // Show info message that photo is ready to be saved
        setStatus({
          type: "info",
          message:
            "Photo selected! Click 'Save Changes' to upload your new profile photo.",
        });
      }
    };
    reader.onerror = () => {
      setStatus({
        type: "error",
        message: "Error reading the file. Please try again.",
      });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: "", message: "" });
    setPasswordErrors({ current: "", new: "", confirm: "" });
    setSaving(true);

    const payload = {};
    let passwordChanged = false;

    // Handle password change via Save Changes button (only for non-OAuth users)
    const wantsPasswordChange =
      !user?.isOAuthUser &&
      !user?.oauthProvider &&
      (currentPassword || newPassword || confirmNewPassword);

    if (wantsPasswordChange) {
      const newErrors = { current: "", new: "", confirm: "" };

      if (!currentPassword) {
        newErrors.current = "Please enter your current password.";
      }
      if (!newPassword) {
        newErrors.new = "Please enter a new password.";
      }
      if (!confirmNewPassword) {
        newErrors.confirm = "Please re-enter your new password.";
      }
      if (!newErrors.new && newPassword.length < 6) {
        newErrors.new = "New password must be at least 6 characters long.";
      }
      if (!newErrors.confirm && newPassword !== confirmNewPassword) {
        newErrors.confirm = "New password and confirmation do not match.";
      }

      if (newErrors.current || newErrors.new || newErrors.confirm) {
        setPasswordErrors(newErrors);
        setSaving(false);
        return;
      }

      try {
        const resp = await axios.put(
          `${API_URL}/auth/change-password`,
          {
            currentPassword,
            newPassword,
          },
          { withCredentials: true },
        );
        passwordChanged = true;
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");

        // If there are no other profile changes, surface password change success
        if (
          formData.name === user?.name &&
          formData.email === (user?.email || "") &&
          !avatarData
        ) {
          setStatus({
            type: "success",
            message: resp.data?.message || "Password changed successfully.",
          });
        }
      } catch (err) {
        const serverMsg = err.response?.data?.message || "";
        const newErrs = { current: "", new: "", confirm: "" };

        if (
          serverMsg.toLowerCase().includes("invalid current password") ||
          serverMsg.toLowerCase().includes("invalid current") ||
          serverMsg.toLowerCase().includes("current password")
        ) {
          newErrs.current =
            "Current password is incorrect. Please try again.";
        } else if (serverMsg) {
          newErrs.current = serverMsg;
        } else {
          newErrs.current =
            "Unable to change password. Please check your current password and try again.";
        }

        setPasswordErrors(newErrs);
        setSaving(false);
        return;
      }
    }

    // If email is verified and locked, don't allow email changes
    if (emailVerified) {
      // Only allow name and avatar updates when email is verified/locked
      if (formData.name !== user?.name) payload.name = formData.name;
      if (formData.role !== user?.role) payload.role = formData.role;
      if (avatarData) payload.avatar = avatarData;
    } else {
      // Normal flow: allow name, avatar, and email changes
      if (formData.name !== user?.name) payload.name = formData.name;
      if (formData.role !== user?.role) payload.role = formData.role;
      if (avatarData) payload.avatar = avatarData;

      // Handle email change separately - requires verification and password
      const emailChanged =
        formData.email !== user?.email && formData.email !== pendingEmail;
      if (emailChanged) {
        // Require password for email changes
        if (!password) {
          setStatus({
            type: "error",
            message:
              "Please enter your current password to change your email address.",
          });
          setSaving(false);
          return;
        }
        payload.email = formData.email;
        payload.password = password; // Include password for verification
      }
    }

    if (Object.keys(payload).length === 0) {
      if (!passwordChanged) {
        setStatus({ type: "info", message: "No changes detected." });
      }
      setSaving(false);
      return;
    }

    const result = await updateProfile(payload);
    if (result.success) {
      if (result.emailVerificationSent) {
        setStatus({
          type: "success",
          message:
            "Verification email sent! Please check your new email address to complete the change. Other updates have been saved.",
        });
        setPendingEmail(formData.email);
        setShowPasswordField(false);
        setPassword("");
      } else {
        // Build a more specific success message based on what was updated
        const updates = [];
        if (payload.name && payload.name !== user?.name) {
          updates.push("name");
        }
        if (payload.role && payload.role !== user?.role) {
          updates.push("role");
        }
        if (payload.avatar) {
          updates.push("profile photo");
        }
        if (payload.email && payload.email !== user?.email) {
          updates.push("email");
        }

        let successMessage = "Profile updated successfully.";
        if (updates.length > 0) {
          if (updates.length === 1) {
            successMessage = `Your ${updates[0]} has been updated successfully.`;
          } else if (updates.length === 2) {
            successMessage = `Your ${updates[0]} and ${updates[1]} have been updated successfully.`;
          } else {
            successMessage = `Your ${updates.slice(0, -1).join(", ")}, and ${updates[updates.length - 1]} have been updated successfully.`;
          }
        }

        // Special message for photo upload
        if (payload.avatar) {
          successMessage =
            "Profile photo uploaded successfully! Your new photo has been saved.";
          if (payload.name && payload.name !== user?.name) {
            successMessage = "Profile photo and name updated successfully!";
          }
        }

        // If password was also changed, mention it in the success message
        if (passwordChanged) {
          successMessage =
            updates.length > 0
              ? `${successMessage} Your password was updated successfully.`
              : "Your password was updated successfully.";
        }

        setStatus({ type: "success", message: successMessage });
        setPendingEmail(null);
        setShowPasswordField(false);
        setPassword("");
        // Clear avatarData after successful upload so it doesn't trigger again
        setAvatarData(null);
      }
    } else {
      // Handle error - check if it's a password error
      const errorMessage = result.message || "Unable to update profile";
      if (
        errorMessage.toLowerCase().includes("password") ||
        errorMessage.toLowerCase().includes("invalid")
      ) {
        setStatus({
          type: "error",
          message:
            "Wrong password. Please enter your correct current password to change your email address.",
        });
        // Don't clear password field on error - let user try again
        // Keep showPasswordField true so they can see the field
      } else {
        setStatus({ type: "error", message: errorMessage });
      }
    }
    setSaving(false);
  };

  /* ---------------------------------------------------------------- helpers
     Presentation-only derivations. No new data is fetched or persisted. */
  const isOAuth = Boolean(user?.isOAuthUser || user?.oauthProvider);
  const visibleSections = SETTINGS_SECTIONS;
  const activeSection =
    visibleSections.find((s) => s.id === activeTab) || visibleSections[0];

  const planLabel =
    subscription?.subscriptionStatus !== "active" &&
    subscription?.subscriptionStatus !== "trialing" &&
    (subscription?.adminAllocatedActionsTotal || 0) > 0
      ? "Admin Invited"
      : subscription?.subscriptionPlan &&
          subscription.subscriptionPlan !== "free"
        ? subscription.subscriptionPlan.charAt(0).toUpperCase() +
          subscription.subscriptionPlan.slice(1)
        : "Free";

  const statusMeta = (() => {
    switch (subscription?.subscriptionStatus) {
      case "trialing":
        return { label: "Free Trial", tone: "accent" };
      case "active":
        return { label: "Active", tone: "success" };
      case "past_due":
        return { label: "Past Due", tone: "danger" };
      case "unpaid":
        return { label: "Payment Failed", tone: "danger" };
      case "canceled":
        return { label: "Canceled", tone: "warning" };
      default:
        return { label: subscription?.subscriptionStatus || "Inactive", tone: "" };
    }
  })();

  const actionsUsed =
    subscription?.isTrialing && subscription?.trialDetails
      ? (subscription.trialDetails.trialAiActionsUsed ?? 0)
      : (subscription?.aiActionsUsed ?? 0);
  const actionsLimit = subscription?.aiMonthlyLimit;
  const usagePct =
    actionsLimit > 0
      ? Math.min(100, Math.round((actionsUsed / actionsLimit) * 100))
      : 0;

  const statusIcon =
    status.type === "success" ? (
      <Check size={16} />
    ) : status.type === "error" ? (
      <AlertTriangle size={16} />
    ) : (
      <Info size={16} />
    );

  const StatusBanner = () =>
    status.message ? (
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
        className={`st-banner st-banner-${status.type || "info"}`}
        role={status.type === "error" ? "alert" : "status"}
      >
        <span className="st-banner-icon">{statusIcon}</span>
        <div>
          <p>{status.message}</p>
          {status.type === "error" && status.message.includes("email") && (
            <p className="st-banner-sub">
              You can still update your name and profile photo below.
            </p>
          )}
        </div>
      </motion.div>
    ) : null;

  const SaveBar = () => (
    <div className="st-savebar">
      <p className="st-savebar-hint">
        Changes apply to your account immediately after saving.
      </p>
      <button type="submit" className="ds-btn ds-btn-primary" disabled={saving}>
        {saving ? (
          <>
            <Loader2 size={15} className="ds-spin" />
            Saving…
          </>
        ) : (
          "Save Changes"
        )}
      </button>
    </div>
  );

  /* ------------------------------------------------------------------ empty */
  if (!user) {
    return (
      <div className="settings-page">
        <Header />
        <div className="st-shell">
          <div className="st-aurora" aria-hidden="true" />
          <div className="st-container">
            <div className="st-skeleton-masthead">
              <div className="ds-skeleton" style={{ height: 30, width: 190 }} />
              <div
                className="ds-skeleton"
                style={{ height: 14, width: 300, marginTop: 12 }}
              />
            </div>
            <div className="st-body">
              <div className="st-nav">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="ds-skeleton"
                    style={{ height: 46, borderRadius: 10, marginBottom: 6 }}
                  />
                ))}
              </div>
              <div className="ds-card st-panel">
                <div className="ds-skeleton" style={{ height: 18, width: 150 }} />
                <div
                  className="ds-skeleton"
                  style={{ height: 12, width: 260, marginTop: 12 }}
                />
                <div
                  className="ds-skeleton"
                  style={{ height: 120, marginTop: 28, borderRadius: 12 }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ render */
  return (
    <div className="settings-page">
      <SEO
        title="Settings"
        path="/settings"
        description="Manage your LawFusion account settings, billing, and subscription."
        noIndex={true}
      />
      <Header />

      <div className="st-shell">
        <div className="st-aurora" aria-hidden="true" />

        <div className="st-container">
          {/* ---------------------------------------------------- masthead */}
          <header className="st-masthead ds-anim-up">
            <div>
              <p className="ds-eyebrow">Workspace</p>
              <h1 className="st-title">Settings</h1>
              <p className="st-subtitle">
                Manage your profile, appearance, security and billing in one place.
              </p>
            </div>

            <div className="st-identity">
              <div className="st-identity-avatar">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="" />
                ) : (
                  <span>{user?.name?.charAt(0)?.toUpperCase() || "U"}</span>
                )}
              </div>
              <div className="st-identity-meta">
                <p className="st-identity-name">{user?.name || "Your account"}</p>
                <p className="st-identity-email">{user?.email}</p>
              </div>
              <span className={`ds-chip ds-chip-${statusMeta.tone || "accent"}`}>
                {planLabel}
              </span>
            </div>
          </header>

          <div className="st-body">
            {/* ------------------------------------------------------- nav */}
            <nav className="st-nav ds-anim-up" aria-label="Settings sections">
              {visibleSections.map(({ id, label, description, Icon }) => {
                const isActive = activeTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setActiveTab(id)}
                    className={`st-nav-item ${isActive ? "is-active" : ""}`}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="st-nav-active"
                        className="st-nav-active"
                        transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
                      />
                    )}
                    <span className="st-nav-icon">
                      <Icon size={16} />
                    </span>
                    <span className="st-nav-text">
                      <span className="st-nav-label">{label}</span>
                      <span className="st-nav-desc">{description}</span>
                    </span>
                  </button>
                );
              })}
            </nav>

            {/* ----------------------------------------------------- panel */}
            <div className="st-panel-wrap">
              <AnimatePresence mode="wait" initial={false}>
                {/* =============================================== ACCOUNT */}
                {activeTab === "account" && (
                  <motion.section key="account" {...panelMotion} className="ds-card st-panel">
                    <div className="st-panel-head">
                      <h2>{activeSection.label}</h2>
                      <p>
                        Update your personal information. Changing your email
                        requires your current password — a verification link is
                        sent to the new address.
                      </p>
                    </div>

                    <form className="st-form" onSubmit={handleSubmit}>
                      {/* avatar */}
                      <div className="st-row st-row-avatar">
                        <div className="st-row-label">
                          <span className="ds-label">Profile photo</span>
                          <span className="ds-hint">PNG or JPG, up to 2MB.</span>
                        </div>
                        <div className="st-avatar-control">
                          <div className="st-avatar">
                            {avatarPreview ? (
                              <img src={avatarPreview} alt="Avatar preview" />
                            ) : (
                              <span>
                                {user?.name?.charAt(0)?.toUpperCase() || "U"}
                              </span>
                            )}
                          </div>
                          <label className="ds-btn ds-btn-secondary ds-btn-sm st-upload">
                            <Camera size={14} />
                            Upload new photo
                            <input
                              type="file"
                              accept="image/png,image/jpeg"
                              onChange={handleAvatarChange}
                            />
                          </label>
                        </div>
                      </div>

                      <hr className="ds-divider st-divider" />

                      {/* name */}
                      <div className="st-row">
                        <div className="st-row-label">
                          <span className="ds-label">Firm, company or your name</span>
                          <span className="ds-hint">
                            Shown on exported research and reports.
                          </span>
                        </div>
                        <div className="st-row-control">
                          <input
                            className="ds-input"
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your full name"
                            required
                          />
                        </div>
                      </div>

                      {/* role */}
                      <div className="st-row">
                        <div className="st-row-label">
                          <span className="ds-label">Role</span>
                          <span className="ds-hint">
                            Helps tailor analysis to your practice.
                          </span>
                        </div>
                        <div className="st-row-control st-select-wrap">
                          <select
                            className="ds-select"
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            required
                          >
                            <option value="Attorney">Attorney</option>
                            <option value="Paralegal">Paralegal</option>
                            <option value="Legal Researcher">Legal Researcher</option>
                            <option value="Other">Other</option>
                          </select>
                          <span className="st-select-chevron" aria-hidden="true" />
                        </div>
                      </div>

                      {/* email */}
                      <div className="st-row">
                        <div className="st-row-label">
                          <span className="ds-label">Email address</span>
                          <span className="ds-hint">
                            Used for sign-in and all account notices.
                          </span>
                        </div>
                        <div className="st-row-control">
                          <div className="st-input-affix">
                            <Mail size={15} className="st-input-icon" />
                            <input
                              className="ds-input st-input-with-icon"
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="you@lawfusion.ai"
                              required
                              disabled={emailVerified}
                              readOnly={emailVerified}
                            />
                            {emailVerified && (
                              <button
                                type="button"
                                className="st-input-action"
                                onClick={() => {
                                  setEmailVerified(false);
                                  setStatus({ type: "", message: "" });
                                }}
                              >
                                Change
                              </button>
                            )}
                          </div>

                          <AnimatePresence initial={false}>
                            {showPasswordField && !emailVerified && (
                              <motion.div
                                key="email-pass"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
                                className="st-reveal"
                              >
                                <div className="ds-field st-nested">
                                  <span className="ds-label">
                                    Current password
                                    <span className="st-req"> — required to change email</span>
                                  </span>
                                  <div className="st-input-affix">
                                    <input
                                      className="ds-input st-input-with-action"
                                      type={showPassword ? "text" : "password"}
                                      value={password}
                                      onChange={(e) => setPassword(e.target.value)}
                                      placeholder="Enter your current password"
                                      required
                                    />
                                    <button
                                      type="button"
                                      className="st-input-eye"
                                      onClick={() => setShowPassword(!showPassword)}
                                      aria-label={
                                        showPassword ? "Hide password" : "Show password"
                                      }
                                    >
                                      {showPassword ? (
                                        <EyeOff size={16} />
                                      ) : (
                                        <Eye size={16} />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {emailVerified && (
                            <div className="st-note st-note-success">
                              <BadgeCheck size={16} />
                              <div>
                                <strong>Email verified.</strong> This address is
                                now fixed. You can still update your name and
                                photo — choose “Change” above to edit it again.
                              </div>
                            </div>
                          )}

                          {!emailVerified &&
                            pendingEmail &&
                            pendingEmail !== user?.email && (
                              <div className="st-note st-note-warning">
                                <Info size={16} />
                                <div>
                                  <strong>Pending change.</strong> A verification
                                  email was sent to <strong>{pendingEmail}</strong>.
                                  Click the link in your inbox to complete it.
                                  <span className="st-note-sub">
                                    Your name and photo can still be updated meanwhile.
                                  </span>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>

                      <StatusBanner />
                      <SaveBar />
                    </form>
                  </motion.section>
                )}

                {/* ============================================ APPEARANCE */}
                {activeTab === "appearance" && (
                  <motion.section key="appearance" {...panelMotion} className="ds-card st-panel">
                    <div className="st-panel-head">
                      <h2>{activeSection.label}</h2>
                      <p>
                        Choose how LawFusion looks. Your preference is saved to
                        this browser and applies across the workspace.
                      </p>
                    </div>

                    <div className="st-row st-row-stack">
                      <div className="st-row-label">
                        <span className="ds-label">Theme</span>
                        <span className="ds-hint">
                          Dark mode reduces glare during long research sessions.
                        </span>
                      </div>

                      <div className="st-theme-grid">
                        {[
                          { id: "light", label: "Light", Icon: Sun, on: !darkMode },
                          { id: "dark", label: "Dark", Icon: Moon, on: darkMode },
                        ].map(({ id, label, Icon, on }) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => setDarkMode(id === "dark")}
                            className={`st-theme-card ${on ? "is-active" : ""}`}
                            aria-pressed={on}
                          >
                            <span className={`st-theme-preview st-theme-preview-${id}`}>
                              <span className="st-theme-bar" />
                              <span className="st-theme-line" />
                              <span className="st-theme-line short" />
                            </span>
                            <span className="st-theme-foot">
                              <Icon size={15} />
                              {label}
                              {on && (
                                <motion.span
                                  layoutId="st-theme-check"
                                  className="st-theme-check"
                                  transition={{ duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
                                >
                                  <Check size={12} />
                                </motion.span>
                              )}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.section>
                )}

                {/* ============================================== SECURITY */}
                {activeTab === "security" && (
                  <motion.section key="security" {...panelMotion} className="ds-card st-panel">
                    <div className="st-panel-head">
                      <h2>{activeSection.label}</h2>
                      <p>
                        {isOAuth
                          ? "Your sign-in is managed by your identity provider."
                          : "Update the password used to sign in to your account."}
                      </p>
                    </div>

                    {isOAuth ? (
                      <div className="st-note st-note-info st-note-block">
                        <ShieldCheck size={18} />
                        <div>
                          <strong>
                            Signed in with{" "}
                            {user?.oauthProvider
                              ? user.oauthProvider.charAt(0).toUpperCase() +
                                user.oauthProvider.slice(1)
                              : "a connected provider"}
                            .
                          </strong>
                          <span className="st-note-sub">
                            Password changes are handled by your provider, so
                            there is nothing to manage here. Your LawFusion
                            account stays protected by their security settings.
                          </span>
                        </div>
                      </div>
                    ) : (
                      <form className="st-form" onSubmit={handleSubmit}>
                        <div className="st-row">
                          <div className="st-row-label">
                            <span className="ds-label">Current password</span>
                            <span className="ds-hint">Confirms it is really you.</span>
                          </div>
                          <div className="st-row-control">
                            <input
                              className={`ds-input ${passwordErrors.current ? "ds-input-invalid" : ""}`}
                              type="password"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              placeholder="Enter your current password"
                              autoComplete="current-password"
                            />
                            {passwordErrors.current && (
                              <p className="ds-error">{passwordErrors.current}</p>
                            )}
                          </div>
                        </div>

                        <div className="st-row">
                          <div className="st-row-label">
                            <span className="ds-label">New password</span>
                            <span className="ds-hint">At least 6 characters.</span>
                          </div>
                          <div className="st-row-control">
                            <input
                              className={`ds-input ${passwordErrors.new ? "ds-input-invalid" : ""}`}
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Enter a new password"
                              autoComplete="new-password"
                            />
                            {passwordErrors.new && (
                              <p className="ds-error">{passwordErrors.new}</p>
                            )}
                          </div>
                        </div>

                        <div className="st-row">
                          <div className="st-row-label">
                            <span className="ds-label">Confirm new password</span>
                            <span className="ds-hint">Must match the new password.</span>
                          </div>
                          <div className="st-row-control">
                            <input
                              className={`ds-input ${passwordErrors.confirm ? "ds-input-invalid" : ""}`}
                              type="password"
                              value={confirmNewPassword}
                              onChange={(e) => setConfirmNewPassword(e.target.value)}
                              placeholder="Re-enter your new password"
                              autoComplete="new-password"
                            />
                            {passwordErrors.confirm && (
                              <p className="ds-error">{passwordErrors.confirm}</p>
                            )}
                          </div>
                        </div>

                        <StatusBanner />
                        <SaveBar />
                      </form>
                    )}
                  </motion.section>
                )}

                {/* =============================================== BILLING */}
                {activeTab === "billing" && (
                  <motion.div key="billing" {...panelMotion} className="st-billing">
                    <section className="ds-card st-panel">
                      <div className="st-panel-head st-panel-head-split">
                        <div>
                          <h2>Plan &amp; billing</h2>
                          <p>View and manage your LawFusion subscription.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate("/pricing")}
                          className="ds-btn ds-btn-secondary ds-btn-sm"
                        >
                          Compare plans
                          <ArrowUpRight size={14} />
                        </button>
                      </div>

                      {(subscription?.subscriptionStatus === "past_due" ||
                        subscription?.subscriptionStatus === "unpaid") && (
                        <div className="st-note st-note-danger st-note-block" role="alert">
                          <AlertTriangle size={18} />
                          <div>
                            <strong>Payment failed.</strong>
                            <span className="st-note-sub">
                              {subscription?.isInPaymentGrace
                                ? "Update your payment method before your grace period ends to avoid losing access."
                                : "Update your payment method to restore full access."}
                            </span>
                            <button
                              type="button"
                              className="ds-btn ds-btn-danger ds-btn-sm"
                              style={{ marginTop: 12 }}
                              onClick={handleManageSubscription}
                            >
                              Update payment method
                            </button>
                          </div>
                        </div>
                      )}

                      {loadingSub && !subscription ? (
                        <div className="st-plan-grid">
                          <div className="ds-skeleton" style={{ height: 168, borderRadius: 14 }} />
                          <div className="ds-skeleton" style={{ height: 168, borderRadius: 14 }} />
                        </div>
                      ) : (
                        <div className="st-plan-grid ds-stagger">
                          {/* plan */}
                          <div className="st-plan-card">
                            <div className="st-plan-top">
                              <span className="ds-eyebrow">Current plan</span>
                              <span className={`ds-chip ds-chip-${statusMeta.tone}`}>
                                <span className={`ds-dot ${
                                  subscription?.subscriptionStatus === "active" ||
                                  subscription?.subscriptionStatus === "trialing"
                                    ? "ds-dot-live"
                                    : ""
                                }`} />
                                {statusMeta.label}
                              </span>
                            </div>
                            <p className="st-plan-name">{planLabel}</p>

                            <div className="st-plan-actions">
                              {!subscription?.hasSubscription && (
                                <button
                                  className="ds-btn ds-btn-primary ds-btn-sm"
                                  onClick={() => navigate("/pricing")}
                                >
                                  Buy plan
                                </button>
                              )}

                              {subscription?.hasSubscription &&
                                (subscription.subscriptionStatus === "active" ||
                                  subscription.subscriptionStatus === "trialing") &&
                                !subscription.paymentAccessBlocked &&
                                subscription.subscriptionStatus !== "past_due" &&
                                subscription.subscriptionStatus !== "unpaid" && (
                                  <>
                                    <button
                                      className="ds-btn ds-btn-primary ds-btn-sm"
                                      onClick={handleManageSubscription}
                                    >
                                      Manage billing
                                    </button>
                                    <button
                                      className="ds-btn ds-btn-secondary ds-btn-sm"
                                      onClick={() =>
                                        navigate("/pricing", {
                                          state: {
                                            switchToPlan:
                                              subscription.subscriptionPlan ===
                                              "professional"
                                                ? "Essentials"
                                                : "Professional",
                                          },
                                        })
                                      }
                                    >
                                      {subscription.subscriptionPlan === "professional"
                                        ? "Switch to Essentials"
                                        : "Switch to Professional"}
                                    </button>
                                    <button
                                      onClick={handleCancelSubscription}
                                      disabled={canceling || subscription?.cancelAtPeriodEnd}
                                      className="ds-btn ds-btn-danger-soft ds-btn-sm"
                                    >
                                      {canceling
                                        ? "Canceling…"
                                        : subscription?.cancelAtPeriodEnd
                                          ? "Cancels at end"
                                          : subscription?.subscriptionStatus === "trialing"
                                            ? "Cancel free trial"
                                            : "Cancel"}
                                    </button>
                                    {subscription?.cancelAtPeriodEnd && (
                                      <button
                                        onClick={handleResumeSubscription}
                                        disabled={resuming}
                                        className="ds-btn ds-btn-primary ds-btn-sm"
                                      >
                                        {resuming
                                          ? "Resuming…"
                                          : "Resume subscription"}
                                      </button>
                                    )}
                                  </>
                                )}

                              {subscription?.hasSubscription &&
                                (subscription.subscriptionStatus === "past_due" ||
                                  subscription.subscriptionStatus === "unpaid") && (
                                  <>
                                    <button
                                      className="ds-btn ds-btn-primary ds-btn-sm"
                                      onClick={handleManageSubscription}
                                    >
                                      Manage billing
                                    </button>
                                    <button
                                      className="ds-btn ds-btn-secondary ds-btn-sm"
                                      onClick={() => navigate("/pricing")}
                                    >
                                      View plans
                                    </button>
                                  </>
                                )}

                              {subscription?.hasSubscription &&
                                subscription.subscriptionStatus === "canceled" && (
                                  <button
                                    className="ds-btn ds-btn-primary ds-btn-sm"
                                    onClick={() => navigate("/pricing")}
                                  >
                                    Upgrade
                                  </button>
                                )}
                            </div>
                          </div>

                          {/* usage */}
                          <div className="st-plan-card">
                            <span className="ds-eyebrow">Usage this period</span>

                            <div className="st-usage">
                              <div className="st-usage-head">
                                <span className="st-usage-value">
                                  {actionsUsed}
                                  <span className="st-usage-limit">
                                    {" "}
                                    / {actionsLimit ?? "—"}
                                  </span>
                                </span>
                                <span className="st-usage-label">
                                  {subscription?.isTrialing
                                    ? "AI actions used (trial)"
                                    : "AI actions used"}
                                </span>
                              </div>
                              <div className="ds-progress st-usage-rail">
                                <motion.div
                                  className="ds-progress-bar"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${usagePct}%` }}
                                  transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
                                />
                              </div>
                            </div>

                            {subscription?.isTrialing && (
                              <p className="st-plan-note">
                                Trials are capped at 20 AI actions regardless of
                                plan. Your full{" "}
                                {subscription?.subscriptionPlan === "professional"
                                  ? 100
                                  : 50}
                                /month limit starts once your trial converts to a
                                paid subscription.
                              </p>
                            )}

                            {(subscription?.subscriptionEndDate ||
                              subscription?.trialDetails?.trialEnd ||
                              subscription?.trialEnd) && (
                              <div className="st-meta-row">
                                <span className="st-meta-label">
                                  {subscription?.isTrialing
                                    ? "Trial ends on"
                                    : "Next billing date"}
                                </span>
                                <span className="st-meta-value">
                                  {subscription?.isTrialing
                                    ? new Date(
                                        subscription?.trialDetails?.trialEnd ||
                                          subscription.trialEnd,
                                      ).toLocaleDateString()
                                    : new Date(
                                        subscription?.subscriptionEndDate,
                                      ).toLocaleDateString()}
                                </span>
                              </div>
                            )}

                            {subscription?.cancelAtPeriodEnd ? (
                              <p className="st-plan-note st-plan-note-danger">
                                {subscription?.isTrialing
                                  ? "Your free trial will end on "
                                  : "Your subscription will cancel on "}
                                {/* While trialing, the date to show is the
                                    trial end — not subscriptionEndDate, which
                                    is currentPeriodEnd (the paid billing period,
                                    ~1 month out). Preferring it here made this
                                    line contradict the "Trial ends on" row
                                    directly above it for the same subscription. */}
                                {new Date(
                                  subscription?.isTrialing
                                    ? subscription.trialDetails?.trialEnd ||
                                      subscription.trialEnd ||
                                      subscription.subscriptionEndDate
                                    : subscription.subscriptionEndDate ||
                                      subscription.trialDetails?.trialEnd ||
                                      subscription.trialEnd,
                                ).toLocaleDateString()}
                                .
                              </p>
                            ) : (
                              subscription?.hasSubscription &&
                              (subscription.subscriptionStatus === "active" ||
                                subscription.subscriptionStatus === "canceled") && (
                                <p className="st-plan-note">
                                  Access continues until the end of your current
                                  billing or trial period if canceled.
                                </p>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </section>

                    {/* token top-up */}
                    {isAuthenticated &&
                      user?.subscriptionStatus === "active" &&
                      (user?.subscriptionPlan === "essentials" ||
                        user?.subscriptionPlan === "professional") && (
                        <section className="ds-card st-panel st-topup">
                          <div className="st-topup-icon" aria-hidden="true">
                            <Zap size={20} />
                          </div>
                          <div className="st-topup-body">
                            <span className="ds-eyebrow">One-time add-on</span>
                            <h3>Need more AI actions?</h3>
                            <p>
                              Top up with 25 extra AI action tokens — no plan
                              change needed.
                            </p>
                            {tokenTopUpError && (
                              <p className="ds-error">{tokenTopUpError}</p>
                            )}
                          </div>
                          <div className="st-topup-action">
                            <p className="st-topup-price">
                              $10
                              <span> / 25 tokens</span>
                            </p>
                            <button
                              className="ds-btn ds-btn-primary"
                              onClick={handleBuyTokens}
                              disabled={buyingTokens}
                            >
                              {buyingTokens ? (
                                <>
                                  <Loader2 size={15} className="ds-spin" />
                                  Redirecting…
                                </>
                              ) : (
                                "Buy 25 tokens"
                              )}
                            </button>
                          </div>
                        </section>
                      )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showCancelConfirm}
        title={
          subscription?.subscriptionStatus === "trialing"
            ? "Cancel Free Trial"
            : "Cancel Subscription"
        }
        message={
          subscription?.subscriptionStatus === "trialing"
            ? "Are you sure you want to cancel your free trial? You will still have access until the end of your trial period."
            : "Are you sure you want to cancel your subscription? You will still have access until the end of your billing period."
        }
        confirmText={canceling ? "Canceling..." : "Cancel Subscription"}
        cancelText="Keep Subscription"
        isDangerous
        isLoading={canceling}
        onConfirm={confirmCancelSubscription}
        onClose={() => !canceling && setShowCancelConfirm(false)}
      />
    </div>
  );
}
